"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { fetchAccount, signOut as signOutRequest, type Account } from "@/lib/account";
import { recordAccountEmail } from "@/lib/posthog";

type AccountContextValue = {
  account: Account | null;
  /** True until the first /me answer lands. The header renders nothing while it is true, so a
      signed-in customer never sees "Sign in" flash before their own name. */
  loading: boolean;
  setAccount: (account: Account | null) => void;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AccountContext = createContext<AccountContextValue | null>(null);

/**
 * Holds the signed-in customer for the whole site, so the header and the account pages agree and
 * only one /me request is made per page load. The session itself lives in an HttpOnly cookie — this
 * is a cache of who that cookie belongs to, never the credential.
 */
export function AccountProvider({ children }: { children: React.ReactNode }) {
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);
  // Which account we have already told PostHog about. Recording it is idempotent but it costs a
  // request, and this provider re-renders on every navigation.
  const identified = useRef<string | null>(null);

  // Every route into an account ends here — password sign-in, sign-up, the reset link, the
  // verification link, and the Google/Facebook callback, which arrives as a plain page load with a
  // cookie. Recording it in one place means none of them can be the one that forgets.
  useEffect(() => {
    if (!account || identified.current === account.id) return;
    identified.current = account.id;
    recordAccountEmail(account.email, {
      account_id: account.id,
      email_verified: account.emailVerified,
      auth_providers: account.providers.join(",") || "password",
    });
  }, [account]);

  const refresh = useCallback(async () => {
    try {
      setAccount(await fetchAccount());
    } catch {
      // A failed /me means "we don't know", which for the header is the same as signed out. The
      // account pages ask again themselves.
      setAccount(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      await signOutRequest();
    } finally {
      setAccount(null);
    }
  }, []);

  const value = useMemo<AccountContextValue>(
    () => ({ account, loading, setAccount, refresh, signOut }),
    [account, loading, refresh, signOut],
  );

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

export function useAccount(): AccountContextValue {
  const value = useContext(AccountContext);
  if (!value) throw new Error("useAccount must be used inside AccountProvider");
  return value;
}
